import type { CSSProperties, ReactElement, ReactNode } from "react";
import { palette, typography, type FontSizeKey, type FontWeightKey } from "@ux/tokens";

export type TextRole =
    | "title"
    | "subtitle"
    | "heading"
    | "subheading"
    | "body"
    | "muted"
    | "label"
    | "eyebrow"
    | "mono";

export interface TextProps {
    readonly role?: TextRole;
    readonly as?: "p" | "span" | "div" | "h1" | "h2" | "h3" | "h4" | "label";
    readonly size?: FontSizeKey;
    readonly weight?: FontWeightKey;
    readonly color?: string;
    readonly htmlFor?: string;
    readonly id?: string;
    readonly style?: CSSProperties;
    readonly children?: ReactNode;
}

export function Text({
    role = "body",
    as,
    size,
    weight,
    color,
    htmlFor,
    id,
    style,
    children,
}: TextProps): ReactElement {
    const Tag = (as ?? defaultElementFor(role)) as keyof JSX.IntrinsicElements;
    const composed: CSSProperties = {
        margin: 0,
        ...defaultStyleFor(role),
        ...(size !== undefined ? { fontSize: typography.size[size] } : {}),
        ...(weight !== undefined ? { fontWeight: typography.weight[weight] } : {}),
        ...(color !== undefined ? { color } : {}),
        ...style,
    };
    const props: Record<string, unknown> = { style: composed };
    if (htmlFor !== undefined) props["htmlFor"] = htmlFor;
    if (id !== undefined) props["id"] = id;
    return <Tag {...props}>{children}</Tag>;
}

function defaultElementFor(role: TextRole): "p" | "span" | "h1" | "h2" | "h3" | "label" {
    switch (role) {
        case "title":
            return "h1";
        case "subtitle":
            return "p";
        case "heading":
            return "h2";
        case "subheading":
            return "h3";
        case "label":
            return "label";
        case "eyebrow":
            return "span";
        case "muted":
            return "p";
        case "mono":
            return "span";
        case "body":
            return "p";
    }
}

function defaultStyleFor(role: TextRole): CSSProperties {
    switch (role) {
        case "title":
            return {
                fontSize: typography.size["3xl"],
                fontWeight: typography.weight.bold,
                letterSpacing: typography.tracking.tight,
                lineHeight: typography.leading.tight,
                color: palette.fg,
            };
        case "subtitle":
            return {
                fontSize: typography.size.md,
                color: palette.fgMuted,
                lineHeight: typography.leading.normal,
            };
        case "heading":
            return {
                fontSize: typography.size.xl,
                fontWeight: typography.weight.semibold,
                letterSpacing: typography.tracking.tight,
                color: palette.fg,
            };
        case "subheading":
            return {
                fontSize: typography.size.lg,
                fontWeight: typography.weight.semibold,
                color: palette.fg,
            };
        case "body":
            return {
                fontSize: typography.size.md,
                color: palette.fg,
                lineHeight: typography.leading.normal,
            };
        case "muted":
            return {
                fontSize: typography.size.sm,
                color: palette.fgMuted,
            };
        case "label":
            return {
                fontSize: typography.size.sm,
                fontWeight: typography.weight.semibold,
                color: palette.fg,
            };
        case "eyebrow":
            return {
                fontSize: typography.size.xs,
                fontWeight: typography.weight.bold,
                letterSpacing: typography.tracking.eyebrow,
                textTransform: "uppercase",
                color: palette.fgSubtle,
            };
        case "mono":
            return {
                fontFamily: typography.family.mono,
                fontSize: typography.size.sm,
                color: palette.fgMuted,
            };
    }
}
